import { useNavigate } from "react-router-dom";
import {
  Activity,
  Receipt,
  TrendingUp,
  BarChart3,
  DollarSign,
  FileText,
} from "lucide-react";
import { ReportsTile } from "./ReportsTile";
import { ROUTES } from "@/routes";

interface ProjectReportsTabProps {
  projectId: number;
}

export function ProjectReportsTab({ projectId }: ProjectReportsTabProps) {
  const navigate = useNavigate();

  const handleProjectStatusClick = () => {
    navigate(`${ROUTES.PROJECT_STATUS_REPORT}?reportProjectId=${projectId}&mode=detail`);
  };

  const handleWipClick = () => {
    navigate(`${ROUTES.WIP_REPORTING}?reportProjectId=${projectId}`);
  };

  const handleBudgetVarianceClick = () => {
    navigate(`${ROUTES.BUDGET_VARIANCE_REPORT}?reportProjectId=${projectId}`);
  };

  const handleProjectPLClick = () => {
    navigate(`${ROUTES.PROJECT_PL_REPORT}?reportProjectId=${projectId}`);
  };

  const handleProjectForecastClick = () => {
    navigate(`${ROUTES.PROJECT_FORECAST_REPORT}?reportProjectId=${projectId}`);
  };

  const handleProfitabilityClick = () => {
    navigate(`${ROUTES.PROFITABILITY}?reportProjectId=${projectId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-base font-semibold">Project Reports</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Access all reports related to this project.
        </p>
      </div>

      {/* Operational Reports */}
      <div className="space-y-3">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Operational Reports
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <ReportsTile
            icon={Activity}
            title="Project Status Report"
            description="Health, schedule, and financial summary. Share with clients or present in governance meetings."
            onClick={handleProjectStatusClick}
          />
          <ReportsTile
            icon={Receipt}
            title="WIP Report"
            description="Work in progress billing position for this project."
            onClick={handleWipClick}
          />
        </div>
      </div>

      {/* Financial Reports */}
      <div className="space-y-3">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Financial Reports
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <ReportsTile
            icon={TrendingUp}
            title="Profit & Loss"
            description="Detailed P&L breakdown including revenue, cost, and margin analysis."
            onClick={handleProjectPLClick}
          />
          <ReportsTile
            icon={BarChart3}
            title="Budget Variance"
            description="Planned vs actual comparison across revenue, cost, and hours budgets."
            onClick={handleBudgetVarianceClick}
          />
          <ReportsTile
            icon={DollarSign}
            title="Project Forecast"
            description="Forward-looking revenue and cost projections to project end."
            onClick={handleProjectForecastClick}
          />

          <ReportsTile
            icon={FileText}
            title="Profitability Analysis"
            description="Portfolio-level gross profit and margin analysis. See which projects are above or below margin thresholds."
            onClick={handleProfitabilityClick}
          />
        </div>
      </div>
    </div>
  );
}
